/**
 * Tally-style inline calculator expression parser.
 *
 * Supports: + - * / %, unary minus, parentheses, decimals.
 * Rejects identifiers, function calls, bitwise ops, assignment, etc.
 * Never uses eval/Function — safe against injection.
 *
 * Usage:
 *   evaluateExpression("100+50*2")        -> { ok: true, value: 200 }
 *   evaluateExpression("1,250.00")        -> { ok: true, value: 1250 }  (commas stripped)
 *   evaluateExpression("100+")            -> { ok: false, error: "..." }
 *   evaluateExpression("1/0")             -> { ok: false, error: "Division by zero" }
 */

const MAX_SAFE = Number.MAX_SAFE_INTEGER;

/**
 * Normalize common paste/IME quirks before parsing:
 * - strip whitespace, commas, currency symbols (₹ $ € £ ¥)
 * - convert unicode minus / en dash / em dash to ASCII "-"
 * - convert unicode multiplication sign to "*"
 * - convert unicode division sign to "/"
 * - convert fullwidth digits to ASCII
 */
export function normalize(input) {
  if (input === null || input === undefined) return "";
  let s = String(input);
  // Fullwidth digits (０-９) → ASCII
  s = s.replace(/[\uFF10-\uFF19]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFEE0));
  // Unicode minuses → ASCII hyphen
  s = s.replace(/[\u2212\u2013\u2014\uFF0D]/g, "-");
  // Unicode ×, ÷
  s = s.replace(/\u00D7/g, "*").replace(/\u00F7/g, "/");
  // Strip commas, currency symbols, whitespace (including unicode)
  s = s.replace(/[,\s\u00A0\u2000-\u200B\u202F\u205F\u3000\u20B9\u0024\u20AC\u00A3\u00A5]/g, "");
  return s;
}

function tokenize(input) {
  const tokens = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === " " || ch === "\t") {
      i++;
      continue;
    }
    if ((ch >= "0" && ch <= "9") || ch === ".") {
      let j = i;
      let dotSeen = ch === ".";
      j++;
      while (j < input.length) {
        const c = input[j];
        if (c >= "0" && c <= "9") {
          j++;
        } else if (c === "." && !dotSeen) {
          dotSeen = true;
          j++;
        } else {
          break;
        }
      }
      const numStr = input.slice(i, j);
      const num = parseFloat(numStr);
      if (!Number.isFinite(num)) {
        throw new Error(`Invalid number: ${numStr}`);
      }
      tokens.push({ type: "num", value: num });
      i = j;
      continue;
    }
    if ("+-*/%()".indexOf(ch) !== -1) {
      tokens.push({ type: "op", value: ch });
      i++;
      continue;
    }
    throw new Error(`Unexpected character: ${ch}`);
  }
  return tokens;
}

function makeParser(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = () => tokens[pos++];

  function parseExpression() {
    let left = parseTerm();
    while (pos < tokens.length) {
      const t = peek();
      if (t.type !== "op" || (t.value !== "+" && t.value !== "-")) break;
      consume();
      const right = parseTerm();
      left = t.value === "+" ? left + right : left - right;
    }
    return left;
  }

  function parseTerm() {
    let left = parseFactor();
    while (pos < tokens.length) {
      const t = peek();
      if (t.type !== "op" || (t.value !== "*" && t.value !== "/" && t.value !== "%")) break;
      consume();
      const right = parseFactor();
      if ((t.value === "/" || t.value === "%") && right === 0) {
        throw new Error("Division by zero");
      }
      if (t.value === "*") left = left * right;
      else if (t.value === "/") left = left / right;
      else left = left % right;
    }
    return left;
  }

  function parseFactor() {
    const t = peek();
    if (!t) throw new Error("Unexpected end of expression");
    if (t.type === "op" && (t.value === "+" || t.value === "-")) {
      consume();
      const v = parseFactor();
      return t.value === "-" ? -v : v;
    }
    return parsePrimary();
  }

  function parsePrimary() {
    const t = consume();
    if (!t) throw new Error("Unexpected end of expression");
    if (t.type === "num") return t.value;
    if (t.type === "op" && t.value === "(") {
      const v = parseExpression();
      const close = consume();
      if (!close || close.type !== "op" || close.value !== ")") {
        throw new Error("Missing closing parenthesis");
      }
      return v;
    }
    throw new Error(`Unexpected token: ${t.value}`);
  }

  return {
    parse() {
      if (tokens.length === 0) throw new Error("Empty expression");
      const value = parseExpression();
      if (pos < tokens.length) {
        throw new Error(`Unexpected token: ${tokens[pos].value}`);
      }
      return value;
    },
  };
}

export function evaluateExpression(input) {
  const cleaned = normalize(input);
  if (cleaned === "") return { ok: false, error: "Empty" };
  try {
    const tokens = tokenize(cleaned);
    if (tokens.length === 0) return { ok: false, error: "Empty" };
    const value = makeParser(tokens).parse();
    if (!Number.isFinite(value)) return { ok: false, error: "Not a finite number" };
    if (Math.abs(value) > MAX_SAFE) return { ok: false, error: "Too large" };
    return { ok: true, value };
  } catch (e) {
    return { ok: false, error: e.message || "Invalid expression" };
  }
}

/**
 * Returns true when the buffer is a well-formed plain number (no operators).
 * Matches: "0", "1", "-1", "1.5", "-1.5", ".5", "-.5", "1.", "-1."
 * Rejects: "", "-", ".", "-.", "1.2.3", "1+2"
 * Used to decide whether to show the live "tape" tooltip.
 */
export function isPlainNumber(input) {
  const cleaned = normalize(input);
  if (cleaned === "") return false;
  return /^-?(\d+\.?\d*|\.\d+)$/.test(cleaned);
}

